import React, { useEffect, useState } from "react";
import api, { money, fmtDate } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { PageHeader, StatusBadge, Spinner } from "@/components/shared";
import { Clock, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const ACTIONS = [["extend", "Extend"], ["return", "Return"], ["donate", "Donate"], ["markdown", "Mark Down"]];

function Bucket({ title, items, tone, cur, onAction, selected, toggle }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
      <div className={`px-4 py-3 border-b border-slate-100 flex items-center gap-2 font-semibold ${tone}`}>
        <AlertCircle className="w-4 h-4" /> {title} <span className="ml-auto text-sm font-mono-fin">{items.length}</span>
      </div>
      <div className="divide-y divide-slate-50 max-h-[420px] overflow-y-auto">
        {items.length === 0 && <p className="text-sm text-slate-400 p-4">Nothing here.</p>}
        {items.map((it) => (
          <div key={it.id} className="px-4 py-3 flex items-center justify-between gap-3" data-testid={`expiring-item-${it.id}`}>
            <div className="flex items-center gap-3 min-w-0">
              <input type="checkbox" checked={!!selected[it.id]} onChange={() => toggle(it.id)} data-testid={`expiring-check-${it.id}`} className="accent-emerald-600" />
              <div className="min-w-0">
                <p className="font-medium text-slate-800 truncate">{it.product_name}</p>
                <p className="text-xs text-slate-400">{it.consignor_name || "Unassigned"} · exp {fmtDate(it.expiration_date)} · {it.days_remaining < 0 ? `${Math.abs(it.days_remaining)}d overdue` : `${it.days_remaining}d left`}</p>
              </div>
            </div>
            <select data-testid={`expiring-action-${it.id}`} onChange={(e) => { if (e.target.value) onAction([it.id], e.target.value); e.target.value = ""; }}
              className="text-xs border border-slate-300 rounded-lg px-2 py-1 bg-white">
              <option value="">Action…</option>{ACTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Expiring() {
  const { store } = useStore();
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState({});
  const [bulkAction, setBulkAction] = useState("");
  const cur = store?.currency || "USD";

  const load = () => { setData(null); api.get("/expiring").then((r) => setData(r.data)); };
  useEffect(() => { load(); }, [store?.id]);

  const doAction = async (ids, action) => {
    try {
      if (ids.length === 1) await api.post(`/items/${ids[0]}/action`, { action });
      else await api.post("/items/bulk-action", { item_ids: ids, action });
      toast.success(`Updated ${ids.length} item(s)`); setSelected({}); load();
    } catch (e) { toast.error("Failed"); }
  };

  const toggle = (id) => setSelected((p) => ({ ...p, [id]: !p[id] }));
  const selectedIds = Object.keys(selected).filter((k) => selected[k]);

  if (!data) return <Spinner />;

  return (
    <div>
      <PageHeader title="Expiring Items" subtitle="Act on items before they expire">
        {selectedIds.length > 0 && (
          <div className="flex items-center gap-2">
            <select data-testid="bulk-action-select" value={bulkAction} onChange={(e) => setBulkAction(e.target.value)} className="px-3 py-2 rounded-lg border border-slate-300 text-sm bg-white">
              <option value="">Bulk action ({selectedIds.length})</option>{ACTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <button data-testid="apply-bulk-btn" disabled={!bulkAction} onClick={() => doAction(selectedIds, bulkAction)}
              className="bg-emerald-700 hover:bg-emerald-800 disabled:opacity-50 text-white font-semibold px-4 py-2 rounded-lg text-sm">Apply</button>
          </div>
        )}
      </PageHeader>
      <div className="grid lg:grid-cols-3 gap-4">
        <Bucket title="Expired" items={data.expired} tone="text-rose-700" cur={cur} onAction={doAction} selected={selected} toggle={toggle} />
        <Bucket title="Expires within 7 days" items={data.within_7} tone="text-amber-700" cur={cur} onAction={doAction} selected={selected} toggle={toggle} />
        <Bucket title="Expires within 30 days" items={data.within_30} tone="text-blue-700" cur={cur} onAction={doAction} selected={selected} toggle={toggle} />
      </div>
    </div>
  );
}
